/**
 * A rule's place in Routing, set from the rule form ("Where should it go?"): which AI
 * notifications list this rule. Saved after the rule itself (a new rule has no id before).
 *
 * A notification that ends up with NO rules and no alert types would catch every alert, so taking
 * the last rule out of one turns it off instead — the rule form says so before it happens.
 */
import type { PolicyDirectory, RoutingPoliciesApi, RoutingPolicy } from './alertActionsModel';

/** The AI notifications that list this rule. */
export function notificationsOfRule(policies: RoutingPolicy[], ruleId: string | null | undefined): string[] {
  if (!ruleId) return [];
  return policies.filter((p) => !p.is_default && (p.alerts.rule_ids || []).includes(String(ruleId))).map((p) => p.id);
}

/** Would taking this rule out leave the notification catching everything? */
export function onlyRuleOf(p: RoutingPolicy, ruleId: string | null | undefined): boolean {
  const rules = p.alerts.rule_ids || [];
  return !!ruleId && rules.length === 1 && rules[0] === String(ruleId) && !(p.alerts.event_types || []).length;
}

/** "Asha, Ravi + 2 steps" — who a notification tells, for a checkbox label. */
export function whoSummary(p: RoutingPolicy, dir: PolicyDirectory | null): string {
  const l = p.levels?.[0];
  if (!l) return 'nobody yet';
  const who = [
    ...l.recipients.users.map((id) => dir?.users.find((u) => u.id === id)?.name || 'a user'),
    ...l.recipients.roles.map((id) => `everyone with the ${dir?.roles.find((r) => r.id === id)?.name || ''} role`),
    ...l.recipients.contacts.map((c) => c.name || c.email || c.phone),
  ];
  const text = who.length ? (who.length > 3 ? `${who.slice(0, 3).join(', ')} +${who.length - 3}` : who.join(', ')) : 'nobody yet';
  const later = (p.levels?.length || 1) - 1;
  return later ? `${text}, then ${later} more step${later > 1 ? 's' : ''} if nobody acknowledges` : text;
}

/**
 * Put the rule in exactly the `selected` notifications. Returns what changed, by name.
 * @param selected - notification ids that should list the rule
 */
export async function applyRuleRouting(api: RoutingPoliciesApi, ruleId: string, selected: string[]): Promise<{ added: string[]; removed: string[]; turnedOff: string[] }> {
  const id = String(ruleId);
  const policies = (await api.list('ai')).filter((p) => !p.is_default);
  const out = { added: [] as string[], removed: [] as string[], turnedOff: [] as string[] };
  for (const p of policies) {
    const rules = p.alerts.rule_ids || [];
    const has = rules.includes(id);
    const want = selected.includes(p.id);
    if (has === want) continue;
    if (want) {
      await api.save(p.id, { alerts: { ...p.alerts, rule_ids: [...rules, id] } });
      out.added.push(p.name);
    } else if (onlyRuleOf(p, id)) {
      await api.save(p.id, { enabled: false });
      out.turnedOff.push(p.name);
    } else {
      await api.save(p.id, { alerts: { ...p.alerts, rule_ids: rules.filter((r) => r !== id) } });
      out.removed.push(p.name);
    }
  }
  return out;
}
